import React, { useEffect, useState } from "react";
import { Star, Quote, BadgeCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

export const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ count: 0, average: 0 });

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/reviews/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  if (reviews.length === 0) return null;

  // Show featured reviews first, then most recent — capped at 6 for the homepage strip.
  const featured = [...reviews]
    .sort((a, b) => (b.featured === true) - (a.featured === true))
    .slice(0, 6);

  return (
    <section data-testid="reviews-section" className="bg-[#fff8ec]">
      <div className="jm-container py-16">
        <div className="text-center">
          <div className="flex justify-center gap-1 text-gold">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className={`w-6 h-6 ${i < Math.round(summary.average) ? "fill-gold" : "fill-none text-navy/20"}`} />
            ))}
          </div>
          <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm mt-4">Happy Customers</p>
          <h2 className="font-display text-4xl sm:text-5xl text-navy mt-2">What people are saying</h2>
          {summary.count > 0 && (
            <p data-testid="reviews-aggregate" className="text-ink/70 mt-3 max-w-xl mx-auto">
              <span className="font-bold text-navy">{summary.average.toFixed(1)} out of 5</span>{" "}
              from {summary.count} verified &amp; moderated review{summary.count === 1 ? "" : "s"}.
            </p>
          )}
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((rev) => (
            <article
              key={rev.id}
              data-testid={`review-card-${rev.id}`}
              className="relative bg-white rounded-lg border border-navy/15 p-6 flex flex-col"
            >
              <Quote className="w-8 h-8 text-gold/40 absolute top-4 right-4" />
              <div className="flex gap-0.5 text-gold">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < rev.rating ? "fill-gold" : "fill-none text-navy/20"}`} />
                ))}
              </div>
              <p className="mt-4 text-ink/80 leading-relaxed flex-1">"{rev.text}"</p>
              <div className="mt-5 flex items-center justify-between gap-2">
                <div>
                  <div className="font-bold text-navy">{rev.author}</div>
                  <div className="text-xs text-ink/50">
                    {rev.location ? `${rev.location} · ` : ""}
                    {rev.created_at ? new Date(rev.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}
                  </div>
                </div>
                {rev.verified && (
                  <span data-testid={`review-verified-${rev.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 bg-green-100 text-green-700 shrink-0">
                    <BadgeCheck className="w-3.5 h-3.5" /> Verified Buyer
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link to="/reviews" data-testid="read-all-reviews-btn"
            className="inline-flex items-center gap-2 bg-navy text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-brick transition-colors">
            Read all reviews <ArrowRight className="w-4 h-4" />
          </Link>
          <a href="/leave-review" data-testid="leave-review-btn"
            className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-6 py-3.5 hover:bg-navy transition-colors">
            <Star className="w-4 h-4 fill-white" /> Leave a Review
          </a>
        </div>
      </div>
    </section>
  );
};
